"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useState } from "react";
import { AuthShell } from "@/components/auth/auth-shell";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/input";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth-store";
import type { TokenResponse } from "@/lib/types";

function safeNext(next: string | null) {
  // Só caminhos internos: evita open redirect via ?next=https://...
  return next && next.startsWith("/") && !next.startsWith("//") ? next : "/hoje";
}

function LoginForm() {
  const router = useRouter();
  const params = useSearchParams();
  const setSession = useAuth((s) => s.setSession);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const data = await api<TokenResponse>("/auth/login", { method: "POST", body: { email, password }, auth: false });
      setSession(data);
      router.replace(safeNext(params.get("next")));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Não foi possível entrar. Tente novamente.");
      setLoading(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <Field label="E-mail" htmlFor="email">
        <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
      </Field>
      <Field label="Senha" htmlFor="password">
        <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
      </Field>
      {error && <p className="rounded-xl bg-danger/10 px-3.5 py-2.5 text-sm text-danger" role="alert">{error}</p>}
      <Button type="submit" size="lg" className="w-full" loading={loading}>
        Entrar
      </Button>
      <p className="text-center text-sm">
        <Link href="/esqueci-senha" className="text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
          Esqueci minha senha
        </Link>
      </p>
    </form>
  );
}

export default function LoginPage() {
  return (
    <AuthShell title="Entrar no Lumë" subtitle="Entre para acompanhar os atendimentos da sua clínica.">
      <Suspense>
        <LoginForm />
      </Suspense>
    </AuthShell>
  );
}
